import { yupResolver } from "@hookform/resolvers/yup";
import { ProductSyncMode, Target } from "api/client.generated";
import * as yup from "yup";

const baseAutocompleteObject = yup.object().nullable().shape({
  id: yup.string().required(),
  label: yup.string(),
});

const connectWiseAutocompleteObjectRequired = baseAutocompleteObject.when(
  ["isBillingSyncEnable", "target"],
  {
    is: (isBillingSyncEnable: boolean, target: string) =>
      isBillingSyncEnable && target === Target.ConnectWise,
    then: (schema) => schema.test("test-required", "Required", (value) => Boolean(value)),
  },
);

const autotaskAutocompleteObjectRequired = baseAutocompleteObject.when(
  ["isBillingSyncEnable", "target"],
  {
    is: (isBillingSyncEnable: boolean, target: string) =>
      isBillingSyncEnable && target === Target.Autotask,
    then: (schema) => schema.test("test-required", "Required", (value) => Boolean(value)),
  },
);

const programEditFormSchema = yup.object().shape({
  target: yup.string(),
  isProgramSyncEnable: yup.boolean().required(),
  emailList: yup
    .array()
    .of(yup.string().required())
    .test("all-emails-valid", "", (emails) => {
      if (!emails) return true;

      const invalidEmails = emails.filter((email) => {
        try {
          yup.string().email().validateSync(email);
          return false;
        } catch {
          return true;
        }
      });

      if (invalidEmails.length > 0) {
        const message =
          `Invalid ${invalidEmails.length === 1 ? "email" : "emails"}: ` +
          `${invalidEmails.map((x) => `'${x}'`).join(", ")}`;

        const error = new yup.ValidationError(message, "", "emailList");
        return new yup.ValidationError(error);
      }
      return true;
    }),
  isBillingSyncEnable: yup.boolean().required(),
  billingMotions: yup
    .array()
    .of(yup.string().required())
    .when("isBillingSyncEnable", {
      is: (isBillingSyncEnable: boolean) => isBillingSyncEnable,
      then: (schema) =>
        schema.test("test-length", "Required", (billingMotions) => Boolean(billingMotions?.length)),
    })
    .required(),
  isProductSyncEnable: yup.boolean().required(),
  productSyncMode: yup.string().required("Required"),
  productBillingModes: yup
    .array()
    .of(yup.string().required())
    .when("productSyncMode", {
      is: (productSyncMode: ProductSyncMode) => productSyncMode === ProductSyncMode.AllProducts,
      then: (schema) => schema.test("test-length", "Required", (modes) => Boolean(modes?.length)),
    })
    .required(),
  isSyncAzureSubscriptionsEnable: yup.boolean(),
  isSyncAnnualSubscriptionsEnable: yup.boolean(),
  isSetNewProductsTaxableEnable: yup.boolean(),
  autotaskBillingCode: autotaskAutocompleteObjectRequired,
  autotaskVendor: baseAutocompleteObject,
  connectWiseProductCategory: connectWiseAutocompleteObjectRequired,
  connectWiseProductSubcategory: connectWiseAutocompleteObjectRequired,
  connectWiseProductType: connectWiseAutocompleteObjectRequired,
  ciqRetailPriceSource: yup.string().required(),
});

export type ProgramEditFormSchemaType = yup.InferType<typeof programEditFormSchema>;
export const ProgramEditFormSchemaResolver = yupResolver(programEditFormSchema);
